import { useEffect, useState } from 'react';
import supabase from './supabaseClient';
import {
  calculateLoggingStreak,
  getUniqueMealDates,
  toLocalDateKey,
  type MealRow,
} from './utils/clientStatsUtils';

interface StreakTrackerProps {
  selectedUserId: string;
  clientName?: string;
}

interface StreakData {
  currentStreak: number;
  longestStreak: number;
  totalDaysLogged: number;
  last7Days: boolean[];
}

export default function StreakTracker({ selectedUserId, clientName }: StreakTrackerProps) {
  const [streakData, setStreakData] = useState<StreakData>({
    currentStreak: 0,
    longestStreak: 0,
    totalDaysLogged: 0,
    last7Days: Array(7).fill(false)
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedUserId) {
      setLoading(false);
      return;
    }

    const calculateStreak = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all meals for the selected client
        const { data: meals, error: fetchError } = await supabase
          .from('Mastersheet')
          .select('meal_time, created_at')
          .eq('telegram_user_id', selectedUserId)
          .order('meal_time', { ascending: false });

        if (fetchError) {
          throw fetchError;
        }

        if (!meals || meals.length === 0) {
          setStreakData({
            currentStreak: 0,
            longestStreak: 0,
            totalDaysLogged: 0,
            last7Days: Array(7).fill(false)
          });
          setLoading(false);
          return;
        }

        const mealRows = (meals || []) as MealRow[];
        const dates = getUniqueMealDates(mealRows);
        const currentStreak = calculateLoggingStreak(mealRows);

        // Calculate longest streak from sorted local dates
        let longestStreak = 0;
        let tempStreak = 0;
        const sortedDates = Array.from(dates).sort();

        for (let i = 0; i < sortedDates.length; i++) {
          if (i === 0) {
            tempStreak = 1;
          } else {
            const prevDate = new Date(sortedDates[i - 1] + 'T12:00:00');
            const currDate = new Date(sortedDates[i] + 'T12:00:00');
            const diffDays = Math.round((currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));

            if (diffDays === 1) {
              tempStreak++;
            } else {
              longestStreak = Math.max(longestStreak, tempStreak);
              tempStreak = 1;
            }
          }
        }
        longestStreak = Math.max(longestStreak, tempStreak);

        const last7Days = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          last7Days.push(dates.has(toLocalDateKey(date)));
        }

        setStreakData({
          currentStreak,
          longestStreak,
          totalDaysLogged: dates.size,
          last7Days
        });

      } catch (err: any) {
        console.error('Error calculating streak:', err);
        setError(err.message || 'Failed to calculate streak');
      } finally {
        setLoading(false);
      }
    };

    calculateStreak();
  }, [selectedUserId]);

  if (loading) {
    return (
      <div className="retro-card p-6">
        <div className="index-tab">
          <h3 className="text-lg font-semibold text-[#1f1f1f]">🔥 Streak Tracker</h3>
        </div>
        <div className="p-4 text-center">
          <div className="text-[#2d2d2d]">Calculating streak...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="retro-card p-6">
        <div className="index-tab">
          <h3 className="text-lg font-semibold text-[#1f1f1f]">🔥 Streak Tracker</h3>
        </div>
        <div className="p-4 text-center">
          <div className="text-red-500">Error: {error}</div>
        </div>
      </div>
    );
  }

  const getStreakEmoji = (streak: number) => {
    if (streak >= 30) return '🔥🔥🔥';
    if (streak >= 14) return '🔥🔥';
    if (streak >= 7) return '🔥';
    if (streak >= 3) return '⚡';
    return '💪';
  };

  const getStreakColor = (streak: number) => {
    if (streak >= 30) return 'text-red-600';
    if (streak >= 14) return 'text-orange-600';
    if (streak >= 7) return 'text-yellow-600';
    if (streak >= 3) return 'text-green-600';
    return 'text-blue-600';
  };

  return (
    <div className="retro-card p-4 md:p-6 pb-32 md:pb-12">
      <div className="index-tab">
        <h3 className="text-base md:text-lg font-semibold text-[#1f1f1f]">
          🔥 Streak Tracker - {clientName || `User: ${selectedUserId}`}
        </h3>
      </div>
      
      <div className="p-3 md:p-4 space-y-4 md:space-y-6">
        {/* Current Streak */}
        <div className="text-center">
          <div className={`text-3xl md:text-4xl font-bold ${getStreakColor(streakData.currentStreak)} mb-2`}>
            {getStreakEmoji(streakData.currentStreak)} {streakData.currentStreak}
          </div>
          <div className="text-base md:text-lg text-[#2d2d2d] font-medium">
            {streakData.currentStreak === 1 ? 'Day' : 'Days'} Logged
          </div>
          <div className="text-xs md:text-sm text-[#666] mt-1">
            {streakData.currentStreak === 0 
              ? "Encourage your client to log meals daily!" 
              : streakData.currentStreak >= 7 
                ? "Your client is showing incredible consistency! 🎉" 
                : "Your client is building great habits!"
            }
          </div>
        </div>

        {/* Weekly Calendar */}
        <div className="space-y-3">
          <div className="text-center text-sm font-medium text-[#2d2d2d]">
            Last 7 Days
          </div>
          <div className="flex justify-center space-x-1 md:space-x-2">
            {streakData.last7Days.map((hasMeal, index) => {
              const date = new Date();
              date.setDate(date.getDate() - (6 - index));
              const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
              
              return (
                <div key={index} className="text-center">
                  <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-xs font-bold mb-1 ${
                    hasMeal 
                      ? 'bg-gradient-to-br from-[#8aff8a] to-[#0eeaff] text-[#1f1f1f] shadow-lg' 
                      : 'bg-[#f0f0f0] text-[#999]'
                  }`}>
                    {hasMeal ? '✓' : '○'}
                  </div>
                  <div className="text-xs text-[#666]">{dayName}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-[#d4d4d4]">
          <div className="text-center">
            <div className="text-xl md:text-2xl font-bold text-[#0eeaff]">
              {streakData.longestStreak}
            </div>
            <div className="text-xs md:text-sm text-[#2d2d2d]">Longest Streak</div>
          </div>
          <div className="text-center">
            <div className="text-xl md:text-2xl font-bold text-[#8aff8a]">
              {streakData.totalDaysLogged}
            </div>
            <div className="text-xs md:text-sm text-[#2d2d2d]">Total Days</div>
          </div>
        </div>

                         {/* Motivation Message */}
        {streakData.currentStreak > 0 && (
          <div className="text-center p-3 bg-gradient-to-r from-[#8aff8a]/20 to-[#0eeaff]/20 rounded-lg border border-[#d4d4d4]">
            <div className="text-xs md:text-sm text-[#2d2d2d] font-medium">
              {streakData.currentStreak >= streakData.longestStreak && streakData.currentStreak > 1
                ? "🏆 Your client has a new personal record! Consider celebrating this milestone!"
                : streakData.currentStreak >= 7
                  ? "🌟 Your client is building excellent habits! This consistency will lead to results!"
                  : "💪 Your client is off to a great start! Every logged day strengthens their commitment!"
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 